import ReactGA from 'react-ga4';
import styled from 'styled-components';
import { ampli } from '../../../../../ampli';
import {
  DirectDealCurationColorType,
  IDirectDealCuration,
} from '../../../../../api/directDealCuration/queries/getDirectDealCurations';
import { Icon } from '../../../../../components/common/icon/Icon';
import { useGetDirectDealCurationsQuery } from '../../../../../hooks/api/directDealCuration/queries/useGetDirectDealCurations.query';
import { useAppDispatch, useAppSelector } from '../../../../../hooks/store/useStore';
import { filterSlice } from '../../../../../store/filterSlice';
import { applyDirectDealCurationFilterCondition } from '../../../../../utils/filter/applyDirectDealCurationFilterCondition.util';

const FILTER_TYPE = 'directDealFilter';

// 카드 색상은 Hard Light 레이어 색으로 입힌다.
const CARD_COLORS: Record<DirectDealCurationColorType, string> = {
  GREY: '#616161',
  BLUE: '#196EFF',
  GREEN: '#1EC66D',
  YELLOW: '#FF8000',
  RED: '#FF3049',
};

export const DirectDealCurationSection = () => {
  const dispatch = useAppDispatch();
  const filterActions = filterSlice.actions;
  const directDealFilters = useAppSelector((state) => state.filter.directDealFilter.globalFilters);

  const { data } = useGetDirectDealCurationsQuery();

  const handleCardClick = (curation: IDirectDealCuration) => {
    ampli.pvmClickOlpCurationCard({ card_title: curation.title });
    ReactGA.event('pvm_click_olp_curation_card', { card_title: curation.title });

    dispatch(
      filterActions.changeFilters({
        filterType: FILTER_TYPE,
        filters: applyDirectDealCurationFilterCondition(directDealFilters, curation.filterCondition),
      }),
    );
  };

  if (!data || data.curations.length === 0) {
    return null;
  }

  // 카드가 홀수 장이면 첫 장을 전체 폭의 가로형으로 보여 준다. (1장일 때도 가로형)
  const hasHorizontalCard = data.curations.length % 2 === 1;

  return (
    <Container>
      {data.curations.map((curation, index) => {
        const isHorizontal = hasHorizontalCard && index === 0;
        // 서버에 카드 색상이 반영되기 전 데이터는 회색으로 보여 준다.
        const cardColor: DirectDealCurationColorType = curation.color in CARD_COLORS ? curation.color : 'GREY';

        return (
          <Card key={curation.id} isHorizontal={isHorizontal} onClick={() => handleCardClick(curation)}>
            <Background>
              <BackgroundImage src={curation.imageUrl} alt={''} isHorizontal={isHorizontal} />
              <ColorBlendLayer />
              <DifferenceBlendLayer />
              <NormalBlendLayer isGrey={cardColor === 'GREY'} />
              <HardLightBlendLayer color={CARD_COLORS[cardColor]} />
            </Background>

            <Information>
              <CountBadgeWrapper>
                <CountBadge>{`매물 ${curation.productCount.toLocaleString()}대`}</CountBadge>
              </CountBadgeWrapper>

              <TitleWrapper isHorizontal={isHorizontal}>
                <SubTitle>{curation.subTitle}</SubTitle>
                {/* 가로형 카드는 타이틀이 한 줄이라, 줄바꿈을 띄어쓰기로 바꿔 이어 붙인다. */}
                <Title isHorizontal={isHorizontal}>
                  {isHorizontal ? curation.title.replace(/\s*\n\s*/g, ' ') : curation.title}
                </Title>
              </TitleWrapper>

              <KeywordsWrapper>
                <Keywords>{curation.keywords.join(' · ')}</Keywords>
                <Icon name={'AngleRight'} size={'Medium'} fill={'#FFFFFF'} />
              </KeywordsWrapper>
            </Information>
          </Card>
        );
      })}
    </Container>
  );
};

const Container = styled.section`
  width: 100%;

  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;

  padding: 0px 16px 24px 16px;
`;

const Card = styled.button<{ isHorizontal: boolean }>`
  height: ${(props) => (props.isHorizontal ? '140px' : '180px')};
  min-width: 0;

  display: flex;
  flex-direction: column;
  grid-column: ${(props) => props.isHorizontal && '1 / -1'};

  position: relative;
  overflow: hidden;

  border-radius: var(--radius-medium, 8px);
  background: #f0f5ff;

  text-align: left;
`;

// 흐림 처리한 이미지 위에 혼합 모드 레이어를 겹쳐 카드 색상을 입힌다.
const Background = styled.div`
  position: absolute;
  inset: 0;
`;

// 시안과 같은 부분이 보이도록, 시안에서 카드 대비 이미지 크기 비율대로 키워 가운데에 둔다.
// (가로형 361x140 카드에 390.9x392.1, 세로형 172.5x180 카드에 269.5x270.3)
const BackgroundImage = styled.img<{ isHorizontal: boolean }>`
  position: absolute;
  top: 50%;
  left: 50%;

  width: ${(props) => (props.isHorizontal ? '108.29%' : '156.22%')};
  height: ${(props) => (props.isHorizontal ? '280.04%' : '150.15%')};
  max-width: none;
  transform: translate(-50%, -50%);

  object-fit: cover;
  filter: blur(8px);
`;

const BlendLayer = styled.div`
  position: absolute;
  inset: 0;
`;

const ColorBlendLayer = styled(BlendLayer)`
  background: #ffffff;
  opacity: 0.6;
  mix-blend-mode: color;
`;

const DifferenceBlendLayer = styled(BlendLayer)`
  background: #ffffff;
  opacity: 0.2;
  mix-blend-mode: difference;
`;

// 시안 기준으로 회색 카드만 흰 레이어를 더 옅게 깐다.
const NormalBlendLayer = styled(BlendLayer)<{ isGrey: boolean }>`
  background: #ffffff;
  opacity: ${(props) => (props.isGrey ? 0.2 : 0.3)};
`;

const HardLightBlendLayer = styled(BlendLayer)<{ color: string }>`
  background: ${(props) => props.color};
  opacity: 0.7;
  mix-blend-mode: hard-light;
`;

const Information = styled.div`
  width: 100%;

  display: flex;
  flex: 1 0 0;
  flex-direction: column;

  position: relative;

  padding: 12px;
`;

const CountBadgeWrapper = styled.div`
  display: flex;
  flex: 1 0 0;
  align-items: flex-start;
`;

const CountBadge = styled.span`
  height: 22px;

  display: flex;
  align-items: center;

  padding: 0px 8px;

  border-radius: var(--radius-small, 4px);
  background: var(--Light-Scheme-Monochrome-white, #fff);

  color: var(--Light-Scheme-Monochrome-black, #232429);

  /* System/Small/s-medium */
  font-family: Pretendard;
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 85%; /* 10.2px */

  white-space: nowrap;
`;

// 세로형 카드는 타이틀이 한 줄이어도 두 줄 높이를 차지해 카드끼리 글자 위치를 맞춘다.
const TitleWrapper = styled.div<{ isHorizontal: boolean }>`
  height: ${(props) => !props.isHorizontal && '80px'};

  display: flex;
  flex-direction: column;
  gap: ${(props) => !props.isHorizontal && '2px'};

  padding: 2px 0px 6px 0px;
  padding-top: ${(props) => props.isHorizontal && '0px'};
`;

const SubTitle = styled.span`
  overflow: hidden;

  color: var(--Light-Scheme-Monochrome-white, #fff);

  /* Display/Medium/m-medium */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 130%; /* 18.2px */

  text-overflow: ellipsis;
  white-space: nowrap;
`;

// 카드 높이가 고정이라 넘치는 타이틀은 말줄임한다. (가로형 1줄, 세로형 2줄)
const Title = styled.span<{ isHorizontal: boolean }>`
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: ${(props) => (props.isHorizontal ? 1 : 2)};
  overflow: hidden;

  padding-top: ${(props) => props.isHorizontal && '2px'};

  color: var(--Light-Scheme-Monochrome-white, #fff);

  /* Display/XX-Large/xxl-bold */
  font-family: Pretendard;
  font-size: 20px;
  font-style: normal;
  font-weight: 700;
  line-height: 130%; /* 26px */

  /* 어드민에서 입력한 줄바꿈을 그대로 보여 준다. */
  white-space: pre-line;
  word-break: keep-all;
`;

const KeywordsWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;

  padding-top: 6px;

  border-top: 1px solid rgba(224, 224, 235, 0.3);
`;

const Keywords = styled.span`
  flex: 1 0 0;
  min-width: 0;
  overflow: hidden;

  color: var(--Light-Scheme-Monochrome-white, #fff);

  /* Display/Medium/m-regular */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 130%; /* 18.2px */

  text-overflow: ellipsis;
  white-space: nowrap;
`;
